import { ReactNode, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Fab,
  Stack,
  Typography,
  TypographyProps,
  IconButton,
  Tooltip,
} from "@mui/material";
import { addSeconds, formatDuration } from "date-fns";
import { capitalize, findIndex, flatten, keyBy, orderBy } from "lodash";
import {
  GetLoadTrackingBoardShipmentsQuery,
  GetShipmentSummariesQuery,
  GoodUnits,
  ShipmentLocationType,
  Status,
  ReasonCodeType,
  TripShipmentLocation,
  useGetReasonCodesQuery,
  useGetShipmentsByIdsQuery,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import StepIndicator from "../../common/StepIndicator";
import { Step } from "../../common/StepIndicator/StepIndicator";
import theme from "../../../theme";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import formatWeight from "../../../utils/labels/formatWeight";
import { formatMileage } from "../../../utils/labels/formatDistance";
import {
  formatDateForLocation,
  formatLocationTimezone,
  formatTimeForLocation,
} from "../../../utils/labels/formatDateTime";
import { AccessAlarm, Edit, Route, Warning } from "@mui/icons-material";
import ShipmentLink from "../../common/ShipmentLink";
import {
  shipmentLocationAddressLabel,
  shipmentLocationLabel,
} from "../../../utils/labels/shipmentLocationLabel";
import AssignmentModalContainer from "../../trip/AssignmentModal";
import { useNavigate } from "react-router-dom";
import { shipmentStatusColors } from "../ShipmentsList/ShipmentsList";
import goodUnitLabel from "../../../utils/labels/goodUnitsLabel";
import { useTranslation } from "react-i18next";
import enumLabel from "../../../utils/labels/enumLabel";
import statusKey from "../../../utils/i18n/statusKey";
import { formatDeliveredQuantity } from "../../../utils/formatDeliveredQuantity";
import { formatLoadedQuantity } from "../../../utils/formatLoadedQuantity";
import { isPickupLocation } from "../../../utils/location/isPickupLocation";
import LoadedQuantityModal from "./LoadedQuantityModal";
import {
  getEffectiveShipmentIds,
  hasMultipleShipments,
} from "../../../utils/shipment/getEffectiveShipmentIds";
import {
  EtaWindowStatus,
  getEtaWindowStatus,
  TripForShipmentLocationUtils,
  TripShipmentLocationWithEta,
} from "../../../utils/trip/getEtaWindowStatus";
import { testIds } from "./ShipmentSummary.testIds";

type ShipmentSummaryRoute = NonNullable<
  GetShipmentSummariesQuery["shipments"]["data"][0]["route"]
>;

export type ShipmentDataSummary = Omit<
  GetShipmentSummariesQuery["shipments"]["data"][0],
  "route"
> & {
  route?:
    | (Omit<ShipmentSummaryRoute, "locations"> & {
        locations?: Array<
          NonNullable<ShipmentSummaryRoute["locations"]>[number] & {
            shipmentLocationIds?: string[] | null;
          }
        > | null;
      })
    | null;
  events?: GetLoadTrackingBoardShipmentsQuery["shipments"]["data"][0]["events"];
  issues?: GetLoadTrackingBoardShipmentsQuery["shipments"]["data"][0]["issues"];
};

export type ShipmentSummaryProps = {
  shipment: ShipmentDataSummary;
  dragable?: boolean;
  assignable?: boolean;
  onClick?: (shipment: ShipmentDataSummary) => void;
  hideHeader?: boolean;
};

type ShipmentLocationTimeProps = {
  shipmentLocation: Omit<
    ShipmentDataSummary["shipmentLocations"][0],
    "timeWindows" | "__typename" | "location"
  > & {
    arrivalTime?: string;
    waitingDuration?: number;
    setupDuration?: number;
    serviceDuration?: number;
    timeWindows?: ShipmentDataSummary["shipmentLocations"][0]["timeWindows"];
  };
  variant?: TypographyProps["variant"];
  hideEndTime?: boolean;
};

export const ShipmentLocationTime = ({
  shipmentLocation,
  variant,
  hideEndTime,
}: ShipmentLocationTimeProps) => {
  const {
    timeWindows,
    arrivalTime,
    waitingDuration,
    setupDuration,
    serviceDuration,
  } = shipmentLocation;
  const startTime =
    arrivalTime || timeWindows?.length
      ? new Date(
          arrivalTime !== undefined
            ? addSeconds(new Date(arrivalTime), waitingDuration || 0)
            : timeWindows?.length
            ? timeWindows[0].fromDate
            : null,
        )
      : null;
  const endTime =
    arrivalTime || timeWindows?.length
      ? new Date(
          arrivalTime !== undefined
            ? addSeconds(
                new Date(arrivalTime),
                (waitingDuration || 0) +
                  (setupDuration || 0) +
                  (serviceDuration || 0),
              )
            : timeWindows?.length
            ? timeWindows[0].toDate
            : null,
        )
      : null;

  if (!startTime || !endTime) {
    return null;
  }
  const isSameDate =
    formatDateForLocation(startTime, shipmentLocation) ===
    formatDateForLocation(endTime, shipmentLocation);
  const toIsoUtc = (value: Date | string | undefined) =>
    value ? new Date(value).toISOString() : undefined;

  return (
    <Typography
      data-testid={testIds.stopTimeWindow}
      data-arrival-time={toIsoUtc(arrivalTime)}
      data-planned-start={toIsoUtc(startTime)}
      data-planned-end={toIsoUtc(endTime)}
      data-waiting-duration-seconds={String(waitingDuration ?? 0)}
      data-setup-duration-seconds={String(setupDuration ?? 0)}
      data-service-duration-seconds={String(serviceDuration ?? 0)}
      data-planned-dwell-seconds={String(
        (setupDuration ?? 0) + (serviceDuration ?? 0),
      )}
      sx={!variant ? { fontSize: 10, fontWeight: 400 } : null}
      variant={variant}
    >
      {formatDateForLocation(startTime, shipmentLocation, false)}
      {", "}
      {formatTimeForLocation(startTime, shipmentLocation, false)}
      {!hideEndTime ? " - " : " "}
      {!isSameDate && !hideEndTime
        ? formatDateForLocation(endTime, shipmentLocation, false) + ", "
        : null}
      {!hideEndTime
        ? formatTimeForLocation(endTime, shipmentLocation)
        : `(${formatLocationTimezone(shipmentLocation)})`}
    </Typography>
  );
};

const etaStatusColor: Record<EtaWindowStatus, string> = {
  green: "success.main",
  yellow: "warning.main",
  red: "error.main",
};

type LiveEtaTimeProps = {
  trip: TripForShipmentLocationUtils;
  shipmentLocation: ShipmentLocationTimeProps["shipmentLocation"] & {
    estimatedArrivalTime?: string | Date | null;
  };
  variant?: TypographyProps["variant"];
};

export const LiveEtaTime = ({
  trip,
  shipmentLocation,
  variant,
}: LiveEtaTimeProps) => {
  const { t } = useTranslation(["trips"]);
  const orgSettingsQuery = useGetOrganizationSettingsQuery();
  const etaEnabled =
    !!orgSettingsQuery.data?.organizationSettings?.dispatching?.enableEta;

  if (!etaEnabled || !shipmentLocation.estimatedArrivalTime) {
    return null;
  }

  const status = getEtaWindowStatus(
    trip,
    shipmentLocation as TripShipmentLocationWithEta,
  );
  if (!status) {
    return null;
  }

  const estimatedArrival = new Date(shipmentLocation.estimatedArrivalTime);

  return (
    <Typography
      sx={
        !variant
          ? { fontSize: 10, fontWeight: 400, color: etaStatusColor[status] }
          : { color: etaStatusColor[status] }
      }
      variant={variant}
    >
      {t("estimatedArrival")}:{" "}
      {formatDateForLocation(estimatedArrival, shipmentLocation, false)}
      {", "}
      {formatTimeForLocation(estimatedArrival, shipmentLocation)}
    </Typography>
  );
};

type ShipmentLocationProps = {
  shipmentLocation: ShipmentLocationTimeProps["shipmentLocation"] & {
    shipmentIds?: string[];
    shipmentId?: string | null;
  };
  allShippedGoodsById: {
    [key: string]:
      | {
          __typename?: "Good" | undefined;
          _id: string;
          label: string;
          unit?: GoodUnits | null;
          quantity: number;
          loadedQuantity?: number | null | undefined;
          weight?: number | null | undefined;
          supplier?: { _id: string; name: string } | null | undefined;
        }
      | undefined;
  };
  hideTime?: boolean;
  centered?: boolean;
  part?: "above" | "below";
  readonly?: boolean;
  trip?: TripForShipmentLocationUtils;
};

export const ShipmentLocationDetails = ({
  allShippedGoodsById,
  shipmentLocation,
  hideTime,
  centered,
  part,
  readonly,
  trip,
}: ShipmentLocationProps) => {
  const { t } = useTranslation(["trips", "orders", "common"]);
  const { shippedGoods, receivedGoods } = shipmentLocation;
  const name = shipmentLocationLabel(shipmentLocation);
  const address = shipmentLocationAddressLabel(shipmentLocation);
  const locationTestId =
    shipmentLocation._id ||
    shipmentLocation.receiver?._id ||
    shipmentLocation.shipper?._id ||
    name ||
    shipmentLocation.locationType;
  const shippedCommodityDataAttributes = (shippedGood: {
    _id: string;
    label: string;
    quantity: number;
    unit?: GoodUnits | null;
    weight?: number | null;
    supplier?: { _id: string; name: string } | null;
  }): Record<string, string | undefined> => ({
    "data-good-direction": "shipped",
    "data-direction": "shipped",
    "data-good-id": shippedGood._id,
    "data-good-label": shippedGood.label,
    "data-good-quantity": String(shippedGood.quantity),
    "data-good-unit": shippedGood.unit ?? GoodUnits.Item,
    "data-good-weight":
      shippedGood.weight === null || shippedGood.weight === undefined
        ? undefined
        : String(shippedGood.weight),
    "data-good-supplier": shippedGood.supplier?.name,
    "data-quantity": String(shippedGood.quantity),
    "data-unit": shippedGood.unit ?? GoodUnits.Item,
    "data-weight":
      shippedGood.weight === null || shippedGood.weight === undefined
        ? undefined
        : String(shippedGood.weight),
    "data-supplier-id": shippedGood.supplier?._id,
    "data-supplier-name": shippedGood.supplier?.name,
  });
  const receivedCommodityDataAttributes = (
    receivedGood: { goodId: string; quantity?: number | null; storageFacilityId?: string | null },
    shippedGood: NonNullable<typeof allShippedGoodsById[string]>,
  ): Record<string, string | undefined> => ({
    "data-good-direction": "received",
    "data-direction": "received",
    "data-good-id": receivedGood.goodId,
    "data-good-label": shippedGood.label,
    "data-good-quantity": String(receivedGood.quantity ?? shippedGood.quantity),
    "data-good-unit": shippedGood.unit ?? GoodUnits.Item,
    "data-good-weight":
      shippedGood.weight === null || shippedGood.weight === undefined
        ? undefined
        : String(shippedGood.weight),
    "data-good-supplier": shippedGood.supplier?.name,
    "data-quantity": String(receivedGood.quantity ?? shippedGood.quantity),
    "data-unit": shippedGood.unit ?? GoodUnits.Item,
    "data-weight":
      shippedGood.weight === null || shippedGood.weight === undefined
        ? undefined
        : String(shippedGood.weight),
    "data-supplier-id": shippedGood.supplier?._id,
    "data-supplier-name": shippedGood.supplier?.name,
    "data-storage-facility": receivedGood.storageFacilityId ?? undefined,
    "data-storage-facility-id": receivedGood.storageFacilityId ?? undefined,
  });
  const [selectedGood, setSelectedGood] =
    useState<{
      good: any;
      isDelivered: boolean;
      shipmentId: string;
      locationId: string;
    } | null>(null);

  // Determine if this is a multi-shipment location
  const isMultiShipment = hasMultipleShipments(
    shipmentLocation as Pick<
      TripShipmentLocation,
      "shipmentIds" | "shipmentId"
    >,
  );

  // Get the effective shipment IDs
  const effectiveShipmentIds = getEffectiveShipmentIds(
    shipmentLocation as Pick<
      TripShipmentLocation,
      "shipmentIds" | "shipmentId"
    >,
  );

  // Always query for shipments based on the location's shipment IDs
  const shouldQuery = effectiveShipmentIds.length > 0;
  const { data: shipmentsData } = useGetShipmentsByIdsQuery(
    {
      shipmentIds: effectiveShipmentIds,
    },
    {
      enabled: shouldQuery,
    },
  );

  // Get all shipments for this location
  const locationShipments = shipmentsData?.shipments?.data || [];

  const { data: reasonCodesData, refetch: refetchReasonCodes } =
    useGetReasonCodesQuery(
      {
        filter: {
          shipmentId: {
            $in: locationShipments.map(s => s._id),
          },
          locationId: shipmentLocation._id,
          type: [
            ReasonCodeType.LoadedQuantityMismatch,
            ReasonCodeType.DeliveredQuantityMismatch,
          ],
        },
      },
      {
        enabled: !!locationShipments.length,
      },
    );

  const reasonCodes = reasonCodesData?.reasonCodes.data || [];

  // Use unified rendering logic for both single and multi-shipment locations
  const firstShipment =
    locationShipments.length > 0 ? locationShipments[0] : null;

  // Check if any shipment in this location has a tripId
  const hasActiveTripId = locationShipments.some((s) => s?.tripId);

  const stopTypeLabel = isPickupLocation(shipmentLocation)
    ? String(t("orders:pickup", "Pickup"))
    : String(t("orders:delivery", "Delivery"));

  const timelineAboveSection = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: centered ? "center" : undefined }}
      >
        {stopTypeLabel}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          textAlign: centered ? "center" : undefined,
        }}
      >
        {name}
      </Typography>
    </Box>
  );

  const formatOrderedGoodLabel = (
    quantity: number,
    unit: GoodUnits | null | undefined,
    label: string,
  ) => `${quantity} ${goodUnitLabel(unit || GoodUnits.Item)} ${label}`;

  const timelineAddressSection = (
    <Typography
      data-testid={testIds.stopAddress}
      sx={{
        fontSize: 13,
        fontWeight: 400,
        textAlign: centered ? "center" : undefined,
      }}
    >
      {address}
    </Typography>
  );

  const timelineOrderLinksSection =
    !isMultiShipment && firstShipment ? (
      <Box sx={{ mt: 0.5, textAlign: centered ? "center" : undefined }}>
        <ShipmentLink shipment={firstShipment} />
      </Box>
    ) : isMultiShipment && locationShipments.length > 1 ? (
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: centered ? "center" : undefined,
        }}
      >
        {locationShipments.map(
          (shipmentItem, index: number) =>
            shipmentItem ? (
              <Box
                key={shipmentItem._id}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <ShipmentLink shipment={shipmentItem} />
                {index < locationShipments.length - 1 ? (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    •
                  </Typography>
                ) : null}
              </Box>
            ) : null,
        )}
      </Box>
    ) : null;

  const hasTimelineCommodities =
    (shippedGoods?.length || 0) + (receivedGoods?.length || 0) > 0;

  const timelineGoodsSection = (() => {
    const commodityItems: ReactNode[] = [];

    (shippedGoods || []).forEach((shippedGood) => {
        const shippedGoodShipment = locationShipments.find((shipment) =>
          shipment?.shipmentLocations?.some((location) =>
            location.shippedGoods?.some(
              (sg) =>
                sg._id === shippedGood._id &&
                sg.quantity === shippedGood.quantity,
            ),
          ),
        );
        const loadedQuantityMismatch = reasonCodes.find(
          (rc) =>
            rc.type === ReasonCodeType.LoadedQuantityMismatch &&
            rc.shipmentId === shippedGoodShipment?._id &&
            rc.locationId === shipmentLocation._id,
        );
        const loadedQuantity =
          shippedGood.loadedQuantity ?? shippedGood.quantity;

        commodityItems.push(
          <Box
            key={`timeline-shipped-${shippedGood._id}`}
            data-testid={testIds.stopCommodity}
            {...shippedCommodityDataAttributes(shippedGood)}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 400 }}>
              {formatOrderedGoodLabel(
                shippedGood.quantity,
                shippedGood.unit,
                shippedGood.label,
              )}
            </Typography>
            {shippedGood.supplier ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  textAlign: centered ? "center" : undefined,
                }}
              >
                {shippedGood.supplier.name}
                {shippedGood.pinCode
                  ? ` (${t(
                      "business:supplier.contract.pinCode",
                      "PIN Code",
                    )}: ${shippedGood.pinCode})`
                  : ""}
              </Typography>
            ) : null}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={centered ? "center" : "flex-start"}
              spacing={0.5}
              sx={{ mt: 0.25 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {t("orders:loadedQuantity", "Loaded")}:{" "}
                {formatLoadedQuantity(loadedQuantity)}{" "}
                {goodUnitLabel(shippedGood.unit || GoodUnits.Item)}
                {loadedQuantityMismatch ? (
                  <Tooltip title={loadedQuantityMismatch.description || ""}>
                    <Warning
                      fontSize="small"
                      sx={{ color: theme.palette.warning.main }}
                    />
                  </Tooltip>
                ) : null}
              </Typography>
              {shippedGoodShipment &&
              hasActiveTripId &&
              isPickupLocation(shipmentLocation) &&
              !readonly ? (
                <IconButton
                  data-testid={`${testIds.shipmentSummaryEditTimelineLoadedQuantity}-${shippedGood._id}`}
                  size="small"
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGood({
                      good: {
                        ...shippedGood,
                        loadedQuantity,
                      },
                      isDelivered: false,
                      shipmentId: shippedGoodShipment._id,
                      locationId: shipmentLocation._id,
                    });
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </Box>,
        );
    });

    (receivedGoods || []).forEach((receivedGood) => {
        const shippedGood = allShippedGoodsById[receivedGood.goodId];
        if (!shippedGood) {
          return;
        }
        const shippedGoodShipment = locationShipments.find((shipment) =>
          shipment?.shipmentLocations?.some((location) =>
            location.shippedGoods?.some(
              (sg) =>
                sg._id === shippedGood._id &&
                sg.quantity === shippedGood.quantity,
            ),
          ),
        );
        const deliveredQuantityMismatch = reasonCodes.find(
          (rc) =>
            rc.type === ReasonCodeType.DeliveredQuantityMismatch &&
            rc.shipmentId === shippedGoodShipment?._id &&
            rc.locationId === shipmentLocation._id,
        );
        const receivedGoodWithFacility =
          receivedGood as typeof receivedGood & {
            storageFacilityId?: string | null;
          };
        const orderedQuantity =
          receivedGoodWithFacility.quantity || shippedGood.quantity;
        const deliveredQuantity =
          receivedGoodWithFacility.deliveredQuantity ?? orderedQuantity;

        commodityItems.push(
          <Box
            key={`timeline-received-${receivedGoodWithFacility.goodId}`}
            data-testid={testIds.stopCommodity}
            {...receivedCommodityDataAttributes(
              receivedGoodWithFacility,
              shippedGood,
            )}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 400 }}>
              {formatOrderedGoodLabel(
                orderedQuantity,
                shippedGood.unit,
                shippedGood.label,
              )}
            </Typography>
            {receivedGoodWithFacility.storageFacilityId ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  textAlign: centered ? "center" : undefined,
                }}
              >
                {t("orders:tank", "Tank")}:{" "}
                {receivedGoodWithFacility.storageFacilityId}
              </Typography>
            ) : null}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={centered ? "center" : "flex-start"}
              spacing={0.5}
              sx={{ mt: 0.25 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {t("orders:deliveredQuantity", "Delivered")}:{" "}
                {formatDeliveredQuantity(deliveredQuantity)}{" "}
                {goodUnitLabel(shippedGood.unit || GoodUnits.Item)}
                {deliveredQuantityMismatch ? (
                  <Tooltip title={deliveredQuantityMismatch.description || ""}>
                    <Warning
                      fontSize="small"
                      sx={{ color: theme.palette.warning.main }}
                    />
                  </Tooltip>
                ) : null}
              </Typography>
              {shippedGoodShipment && hasActiveTripId && !readonly ? (
                <IconButton
                  data-testid={`${testIds.shipmentSummaryEditTimelineDeliveredQuantity}-${receivedGood.goodId}`}
                  size="small"
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGood({
                      good: {
                        ...shippedGood,
                        quantity: orderedQuantity,
                        loadedQuantity:
                          shippedGood.loadedQuantity || shippedGood.quantity,
                        deliveredQuantity,
                        storageFacilityId:
                          receivedGoodWithFacility.storageFacilityId ?? null,
                      },
                      isDelivered: true,
                      shipmentId: shippedGoodShipment._id,
                      locationId: shipmentLocation._id,
                    });
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </Box>,
        );
    });

    if (commodityItems.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          width: "100%",
          textAlign: centered ? "center" : "left",
        }}
      >
        {commodityItems.map((item, index) => (
          <Box key={`timeline-commodity-${index}`}>
            {index > 0 ? <Divider sx={{ my: 1 }} /> : null}
            {item}
          </Box>
        ))}
      </Box>
    );
  })();

  const goodsSection = (
    <Box>
        {(shippedGoods || []).map((shippedGood) => {
          const shippedGoodShipment = locationShipments.find(
            (shipment) =>
              shipment?.shipmentLocations?.some((location) =>
                location.shippedGoods?.some(
                  (sg) =>
                    sg._id === shippedGood._id &&
                    sg.quantity === shippedGood.quantity,
                ),
              ),
          );
          const loadedQuantityMismatch = reasonCodes.find(
            (rc) =>
              rc.type === ReasonCodeType.LoadedQuantityMismatch &&
              rc.shipmentId === shippedGoodShipment?._id &&
              rc.locationId === shipmentLocation._id,
          );

          return (
            <Stack
              key={`shipped-${shippedGood._id}`}
              {...shippedCommodityDataAttributes(shippedGood)}
              data-testid={`${testIds.shipmentSummaryShippedGood}-${locationTestId}-${shippedGood._id}`}
              data-good-direction="shipped"
              data-good-id={shippedGood._id}
              data-good-label={shippedGood.label}
              direction="row"
              alignItems="flex-start"
              spacing={0.5}
              sx={{ textAlign: centered ? "center" : undefined }}
            >
              <Box
                data-testid={testIds.stopCommodity}
                {...shippedCommodityDataAttributes(shippedGood)}
                sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                  }}
                >
                  {shippedGood.label}
                  {shippedGood.supplier
                    ? ` (Supplier: ${shippedGood.supplier.name})`
                    : ""}{" "}
                  ({shippedGood.quantity}{" "}
                  {goodUnitLabel(shippedGood.unit || GoodUnits.Item)} x{" "}
                  {formatWeight(shippedGood.weight)})
                  {shippedGood.pinCode
                    ? ` (${t(
                        "business:supplier.contract.pinCode",
                        "PIN Code",
                      )}: ${shippedGood.pinCode})`
                    : ""}
                </Typography>
                {shippedGood.loadedQuantity && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.secondary,
                      fontStyle: "italic",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {t("orders:loadedQuantity", "Loaded")}:{" "}
                    {formatLoadedQuantity(shippedGood.loadedQuantity)}{" "}
                    {goodUnitLabel(shippedGood.unit || GoodUnits.Item)}
                    {loadedQuantityMismatch && (
                      <Tooltip title={loadedQuantityMismatch.description || ""}>
                        <Warning
                          fontSize="small"
                          sx={{ color: theme.palette.warning.main }}
                        />
                      </Tooltip>
                    )}
                  </Typography>
                )}
              </Box>
              {shippedGoodShipment &&
                hasActiveTripId &&
                isPickupLocation(shipmentLocation) &&
                !readonly && (
                  <IconButton data-testid={`${testIds.shipmentSummaryEditLoadedQuantity}-${shippedGood._id}`}
                    size="small"
                    sx={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGood({
                        good: {
                          ...shippedGood,
                          loadedQuantity:
                            shippedGood.loadedQuantity || shippedGood.quantity,
                        },
                        isDelivered: false,
                        shipmentId: shippedGoodShipment._id,
                        locationId: shipmentLocation._id,
                      });
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
            </Stack>
          );
        })}
        {(receivedGoods || []).map((receivedGood, receivedGoodIndex) => {
          const shippedGood = allShippedGoodsById[receivedGood.goodId];
          if (!shippedGood) {
            return null;
          }
          const shippedGoodShipment = locationShipments.find(
            (shipment) =>
              shipment?.shipmentLocations?.some((location) =>
                location.shippedGoods?.some(
                  (sg) =>
                    sg._id === shippedGood._id &&
                    sg.quantity === shippedGood.quantity,
                ),
              ),
          );

          const deliveredQuantityMismatch = reasonCodes.find(
            (rc) =>
              rc.type === ReasonCodeType.DeliveredQuantityMismatch &&
              rc.shipmentId === shippedGoodShipment?._id &&
              rc.locationId === shipmentLocation._id,
          );

          // Type assertion to include storageFacilityId
          const receivedGoodWithFacility =
            receivedGood as typeof receivedGood & {
              storageFacilityId?: string | null;
              netDeliveredQuantity?: number | null;
            };

          return (
            <Stack
              key={`received-${receivedGoodWithFacility.goodId}-${receivedGoodWithFacility.storageFacilityId ?? "default"}-${receivedGoodIndex}`}
              {...receivedCommodityDataAttributes(
                receivedGoodWithFacility,
                shippedGood,
              )}
              data-testid={`${testIds.shipmentSummaryReceivedGood}-${locationTestId}-${receivedGoodWithFacility.goodId}-${receivedGoodWithFacility.storageFacilityId ?? "default"}`}
              data-good-direction="received"
              data-good-id={receivedGoodWithFacility.goodId}
              data-good-label={shippedGood.label}
              data-storage-facility={
                receivedGoodWithFacility.storageFacilityId ?? undefined
              }
              direction="row"
              alignItems="flex-start"
              spacing={0.5}
              sx={{
                fontSize: 10,
                fontWeight: 400,
                textAlign: centered ? "center" : undefined,
              }}
            >
              <Box
                data-testid={testIds.stopCommodity}
                {...receivedCommodityDataAttributes(
                  receivedGoodWithFacility,
                  shippedGood,
                )}
                sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                  }}
                >
                  {shippedGood.label}
                  {receivedGoodWithFacility.storageFacilityId && (
                    <span style={{ color: theme.palette.text.secondary }}>
                      {" "}
                      ({receivedGoodWithFacility.storageFacilityId})
                    </span>
                  )}{" "}
                  ({receivedGoodWithFacility.quantity || shippedGood.quantity}{" "}
                  {goodUnitLabel(shippedGood.unit || GoodUnits.Item)} x{" "}
                  {formatWeight(shippedGood.weight)})
                </Typography>
                {receivedGoodWithFacility.deliveredQuantity != null && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.secondary,
                      fontStyle: "italic",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {t("orders:deliveredQuantity", "Delivered")}:{" "}
                    {formatDeliveredQuantity(
                      receivedGoodWithFacility.deliveredQuantity,
                    )}{" "}
                    {goodUnitLabel(shippedGood.unit || GoodUnits.Item)}
                    {deliveredQuantityMismatch && (
                      <Tooltip
                        title={deliveredQuantityMismatch.description || ""}
                      >
                        <Warning
                          fontSize="small"
                          sx={{ color: theme.palette.warning.main }}
                        />
                      </Tooltip>
                    )}
                  </Typography>
                )}
              </Box>
              {shippedGoodShipment && hasActiveTripId && !readonly && (
                <IconButton data-testid={`${testIds.shipmentSummaryEditDeliveredQuantity}-${receivedGood.goodId}`}
                  size="small"
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGood({
                      good: {
                        ...shippedGood,
                        // Use the receivedGood quantity which is specific to this storage facility
                        quantity:
                          receivedGoodWithFacility.quantity ||
                          shippedGood.quantity,
                        loadedQuantity:
                          shippedGood.loadedQuantity || shippedGood.quantity,
                        deliveredQuantity:
                          receivedGoodWithFacility.deliveredQuantity,
                        netDeliveredQuantity:
                          receivedGoodWithFacility.netDeliveredQuantity,
                        storageFacilityId:
                          receivedGoodWithFacility.storageFacilityId ?? null,
                      },
                      isDelivered: true,
                      shipmentId: shippedGoodShipment._id,
                      locationId: shipmentLocation._id,
                    });
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        })}
    </Box>
  );

  const timelineBelowSection = (
    <>
      {timelineAddressSection}
      {!hideTime ? (
        <Box sx={{ mt: 0.5 }} textAlign={centered ? "center" : "left"}>
          <ShipmentLocationTime shipmentLocation={shipmentLocation} />
          {trip ? (
            <LiveEtaTime trip={trip} shipmentLocation={shipmentLocation} />
          ) : null}
        </Box>
      ) : null}
      {timelineOrderLinksSection}
      {timelineOrderLinksSection && hasTimelineCommodities ? (
        <Divider sx={{ my: 1 }} />
      ) : null}
      {timelineGoodsSection}
    </>
  );

  if (part === "above") {
    return <Box sx={{ width: "100%" }}>{timelineAboveSection}</Box>;
  }

  if (part === "below") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%" }}>
        {timelineBelowSection}
        {selectedGood && firstShipment?.tripId ? (
          <LoadedQuantityModal
            open={!!selectedGood}
            onClose={() => {
              setSelectedGood(null);
              refetchReasonCodes();
            }}
            shipmentId={selectedGood.shipmentId}
            tripId={firstShipment.tripId}
            shipmentLocationId={selectedGood.locationId}
            good={selectedGood.good}
            isLoaded={!selectedGood.isDelivered}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: hideTime && centered ? "center" : "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textAlign: centered ? "center" : undefined,
            }}
          >
            {name}
            {!isMultiShipment && firstShipment ? <br /> : null}
            {!isMultiShipment && firstShipment ? " " : null}
            {!isMultiShipment && firstShipment ? (
              <ShipmentLink shipment={firstShipment} />
            ) : null}
          </Typography>
          {isMultiShipment && locationShipments.length > 1 && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {locationShipments.map(
                (shipmentItem, index: number) =>
                  shipmentItem ? (
                    <Box
                      key={shipmentItem._id}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <ShipmentLink shipment={shipmentItem} />
                      {index < locationShipments.length - 1 && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          •
                        </Typography>
                      )}
                    </Box>
                  ) : null,
              )}
            </Box>
          )}
        </Box>
        {!hideTime ? (
          <Box sx={{ ml: 2 }} textAlign="right">
            <ShipmentLocationTime shipmentLocation={shipmentLocation} />
          </Box>
        ) : null}
      </Box>
      <Typography
        data-testid={testIds.stopAddress}
        sx={{
          fontSize: 13,
          fontWeight: 400,
          textAlign: centered ? "center" : undefined,
        }}
      >
        {address}
      </Typography>
      {goodsSection}

      {selectedGood && firstShipment?.tripId ? (
        <LoadedQuantityModal
          open={!!selectedGood}
          onClose={() => {
            setSelectedGood(null);
            refetchReasonCodes();
          }}
          shipmentId={selectedGood.shipmentId}
          tripId={firstShipment.tripId}
          shipmentLocationId={selectedGood.locationId}
          good={selectedGood.good}
          isLoaded={!selectedGood.isDelivered}
        />
      ) : null}
    </Box>
  );
};

const ShipmentSummary = ({
  shipment,
  onClick,
  assignable,
  hideHeader = false,
}: ShipmentSummaryProps) => {
  const { t } = useTranslation(["trips", "orders"]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const navigate = useNavigate();
  const allShippedGoodsById = keyBy(
    flatten(
      shipment.shipmentLocations.map((location) => location.shippedGoods || []),
    ),
    "_id",
  );

  const orderedShipmentLocations = useMemo(() => {
    return orderBy(shipment.shipmentLocations, (shipmentLocation) => {
      // A merged route stop keeps one name, all its stops are in shipmentLocationIds
      const mergedStopIndex = findIndex(
        shipment.route?.locations,
        (routeLocation) =>
          !!routeLocation.shipmentLocationIds?.includes(shipmentLocation._id),
      );
      if (mergedStopIndex !== -1) {
        return mergedStopIndex;
      }
      return findIndex(
        shipment.route?.locations,
        (routeLocation) =>
          routeLocation._id === shipmentLocation._id ||
          (!!shipmentLocation.shipper &&
            shipmentLocation.shipper?._id === routeLocation.shipper?._id) ||
          (!!shipmentLocation.receiver &&
            shipmentLocation.receiver?._id === routeLocation.receiver?._id),
      );
    });
  }, [shipment.shipmentLocations, shipment.route?.locations]);

  const shipmentLocationsCount = orderedShipmentLocations.length;
  const firstShipmentLocation = orderedShipmentLocations[0];
  const lastShipmentLocation =
    orderedShipmentLocations[shipmentLocationsCount - 1];
  const separatorLength = shipmentLocationsCount > 2 && !isExpanded ? 20 : 40;

  const stopDataAttributes = (
    loc: typeof firstShipmentLocation
  ): Record<string, string> => {
    if (!loc) return {};
    return {
      "data-testid": `order-stop-${loc._id}`,
      "data-stop-role":
        loc.locationType === ShipmentLocationType.Pickup
          ? "shipper"
          : "receiver",
      "data-has-tank": String(
        (loc.receivedGoods || []).some(
          (rg) =>
            !!(rg as { storageFacilityId?: string | null }).storageFacilityId
        )
      ),
      "data-stop-name": loc.receiver?.name ?? loc.shipper?.name ?? "",
    };
  };

  const betweenSteps: Step[] =
    shipmentLocationsCount > 2 && !isExpanded
      ? [
          {
            id: "between-indicator",
            indicator: (
              <Typography
                data-testid={testIds.shipmentSummaryIntermediateStops}
                data-hidden-stop-count={String(shipmentLocationsCount - 2)}
                sx={{
                  fontSize: 8,
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >{`+${shipmentLocationsCount - 2}`}</Typography>
            ),
            color: theme.palette.primary.main,
            separatorLength: 20,
            onIndicatorClick: () => setIsExpanded(true),
          },
        ]
      : orderedShipmentLocations
          .slice(1, shipmentLocationsCount - 1)
          .map((_shipmentLocation) => ({
            id:
              _shipmentLocation.receiver?._id ||
              _shipmentLocation.shipper?._id ||
              "",
            content: (
              <ShipmentLocationDetails
                allShippedGoodsById={allShippedGoodsById}
                shipmentLocation={{
                  ..._shipmentLocation,
                  shipmentId: shipment._id,
                }}
              />
            ),
            color:
              _shipmentLocation.locationType === ShipmentLocationType.Pickup
                ? theme.palette.primary.main
                : theme.palette.accent.main,
            separatorLength: 40,
            dataAttributes: stopDataAttributes(_shipmentLocation),
          }));

  const firstStep: Step = {
    id: "firststep-indicator",
    hideFirstLine: true,
    content: (
      <ShipmentLocationDetails
        allShippedGoodsById={allShippedGoodsById}
        shipmentLocation={{
          ...firstShipmentLocation,
          shipmentId: shipment._id,
        }}
      />
    ),
    color: theme.palette.primary.main,
    separatorLength: separatorLength,
    dataAttributes: stopDataAttributes(firstShipmentLocation),
  };
  const lastStep: Step = {
    id: "laststep-indicator",
    hideLastLine: true,
    content: (
      <ShipmentLocationDetails
        allShippedGoodsById={allShippedGoodsById}
        shipmentLocation={{
          ...lastShipmentLocation,
          shipmentId: shipment._id,
        }}
      />
    ),
    color: theme.palette.accent.main,
    separatorLength: separatorLength,
    dataAttributes: stopDataAttributes(lastShipmentLocation),
  };

  const steps: Step[] = [firstStep, ...betweenSteps, lastStep];

  const shipmentPotentialDelayIssue = shipment.issues?.find(
    (issue) => issue.type === "POTENTIAL_DELAY",
  );

  const shipmentActualDelayIssue = shipment.issues?.find(
    (issue) => issue.type === "DELAY",
  );

  const shipmentDelayIssue =
    shipmentActualDelayIssue || shipmentPotentialDelayIssue;

  return (
    <Box data-testid={`${testIds.shipmentSummaryOpenDetails}-${shipment._id}`}
      sx={(theme) => ({
        paddingTop: 2,
        backgroundColor: theme.palette.primaryBackground.main,
        position: "relative",
        cursor: onClick ? "pointer" : undefined,
        borderWidth: shipmentDelayIssue ? 5 : undefined,
        borderColor: shipmentActualDelayIssue
          ? theme.palette.error.main
          : shipmentPotentialDelayIssue
          ? theme.palette.accent.main
          : undefined,
        borderStyle: shipmentDelayIssue ? "solid" : undefined,
      })}
      onClick={() => onClick && onClick(shipment)}
    >
      {!hideHeader && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          sx={{
            pl: 2,
            pr: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {shipment.customer?.name}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {shipment.status !== Status.WaitingForAssignment &&
            shipment.status !== Status.Assigned ? (
              <Chip
                label={capitalize(
                  t("orders:statusLabel", {
                    status: statusKey[shipment.status],
                    defaultValue: enumLabel(shipment.status),
                  }),
                )}
                color={shipmentStatusColors[shipment.status]}
              />
            ) : null}
            <ShipmentLink shipment={shipment} />{" "}
            {assignable ? (
              <>
                {shipment.tripId ? (
                  <Button data-testid={testIds.shipmentSummaryOpenTrip}
                    variant="contained"
                    color="lightPrimary"
                    size="small"
                    href={`/trips/details/${shipment.tripId}`}
                    startIcon={<Route />}
                  >
                    {t("trips:open", "Open Trip")}
                  </Button>
                ) : null}
                <Button data-testid={testIds.shipmentSummaryAssign}
                  variant="outlined"
                  color="lightPrimary"
                  size="small"
                  onClick={() => {
                    setIsAssignmentModalOpen(true);
                  }}
                  id="assign-button"
                >
                  {t("trips:assign", "Assign")}
                </Button>
                <AssignmentModalContainer
                  tripId={shipment.tripId}
                  shipments={[shipment]}
                  open={isAssignmentModalOpen}
                  onClose={(tripId) => {
                    setIsAssignmentModalOpen(false);
                    if (tripId) {
                      navigate(`/trips/details/${tripId}`);
                    }
                  }}
                />
              </>
            ) : null}
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: "calc(50% - 20px)",
          right: 30,
        }}
      >
        {shipmentDelayIssue ? (
          <Chip
            icon={<AccessAlarm />}
            label={`${
              shipmentActualDelayIssue ? "Delayed" : "Potential delay"
            } ${
              shipmentDelayIssue.delayAmount
                ? formatDuration({
                    hours: Math.floor(shipmentDelayIssue.delayAmount / 3600),
                    minutes: Math.floor(
                      (shipmentDelayIssue.delayAmount % 3600) / 60,
                    ),
                  })
                : ""
            }`}
            color={shipmentActualDelayIssue ? "error" : "warning"}
          />
        ) : !isExpanded && shipment.route?.routeDistance ? (
          <Typography
            textAlign={"center"}
            color="primary"
            sx={{
              fontWeight: 400,
              fontSize: 13,
            }}
          >
            {formatMileage(shipment.route?.routeDistance)}
          </Typography>
        ) : null}
      </Box>
      <StepIndicator steps={steps} />
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {isExpanded && (
          <Fab data-testid={testIds.shipmentSummaryCollapse}
            color="primary"
            size="small"
            sx={{ marginBottom: -2 }}
            onClick={() => setIsExpanded(false)}
            aria-label="toggle-wide"
          >
            <ExpandLessOutlinedIcon sx={{ color: "white" }} />
          </Fab>
        )}
      </Box>
    </Box>
  );
};

export default ShipmentSummary;
